import type { Locale } from "@/context/AppProviders";
import { normalizeTagSlug } from "@/lib/slug";
import { SITE_CONTENT } from "@/content/site";

export function slugifyFeature(title: string): string {
  const map: Record<string, string> = {
    ç: "c",
    ğ: "g",
    ı: "i",
    ö: "o",
    ş: "s",
    ü: "u",
    Ç: "c",
    Ğ: "g",
    İ: "i",
    Ö: "o",
    Ş: "s",
    Ü: "u",
    "&": "ve",
  };

  return title
    .split("")
    .map((ch) => map[ch] ?? ch)
    .join("")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export const LOCALES = ["tr", "en"] as const;

export const SERVICE_IDS = [
  "ecommerce",
  "quickSale",
  "dealer",
  "accounting",
  "website",
] as const;

export type ServiceId = (typeof SERVICE_IDS)[number];

const SERVICE_SLUGS: Record<Locale, Record<ServiceId, string>> = {
  tr: {
    ecommerce: "e-ticaret",
    quickSale: "hizli-satis",
    dealer: "bayi-toptan",
    accounting: "on-muhasebe",
    website: "web-site",
  },
  en: {
    ecommerce: "e-commerce",
    quickSale: "quick-sale",
    dealer: "dealer-wholesale",
    accounting: "accounting",
    website: "website",
  },
};

const STATIC_SEGMENTS: Record<
  Locale,
  {
    services: string;
    pricing: string;
    integrations: string;
    blog: string;
    contact: string;
    features: string;
    about: string;
    privacy: string;
    terms: string;
  }
> = {
  tr: {
    services: "servisler",
    pricing: "fiyatlandirma",
    integrations: "entegrasyonlar",
    blog: "blog",
    contact: "iletisim",
    features: "ozellikler",
    about: "hakkimizda",
    privacy: "gizlilik",
    terms: "kullanim-sozlesmesi",
  },
  en: {
    services: "services",
    pricing: "pricing",
    integrations: "integrations",
    blog: "blog",
    contact: "contact",
    features: "features",
    about: "about",
    privacy: "privacy",
    terms: "terms",
  },
};

export function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value);
}

export function hasBlog(locale: Locale): boolean {
  return locale === "tr";
}

export function parseLocale(pathname: string): Locale | null {
  const match = pathname.match(/^\/(tr|en)(?:\/|$)/);
  if (!match) return null;
  return match[1] as Locale;
}

export function detectInitialLocale(): Locale {
  const stored = localStorage.getItem("ena_lang");
  if (stored === "en" || stored === "tr") return stored;

  const lang = navigator.language.toLowerCase();
  return lang.startsWith("tr") ? "tr" : "en";
}

export function homePath(locale: Locale): string {
  return `/${locale}`;
}

export function getServiceIdFromSlug(locale: Locale, slug: string): ServiceId | null {
  const entry = Object.entries(SERVICE_SLUGS[locale]).find(([, value]) => value === slug);
  return entry ? (entry[0] as ServiceId) : null;
}

export function getServiceSlug(locale: Locale, serviceId: ServiceId): string {
  return SERVICE_SLUGS[locale][serviceId];
}

export function getServiceIndex(serviceId: ServiceId): number {
  return SERVICE_IDS.indexOf(serviceId);
}

export function getFeatureSlug(locale: Locale, serviceId: ServiceId, featureIndex: number): string {
  const index = getServiceIndex(serviceId);
  const title = SITE_CONTENT[locale].mods[index]?.items[featureIndex]?.title ?? "";
  return slugifyFeature(title);
}

/** Eski URL slug'ları → güncel slug (SEO yönlendirmesi için) */
const FEATURE_SLUG_ALIASES: Record<string, string> = {
  "online-magaza-ve-vitrin": "enabase-online-magaza",
  "online-store-ve-storefront": "enabase-online-store",
};

export function resolveFeatureSlug(featureSlug: string): string {
  return FEATURE_SLUG_ALIASES[featureSlug] ?? featureSlug;
}

export function getFeatureIndexFromSlug(
  locale: Locale,
  serviceId: ServiceId,
  featureSlug: string
): number | null {
  const index = getServiceIndex(serviceId);
  const items = SITE_CONTENT[locale].mods[index]?.items ?? [];
  const canonical = resolveFeatureSlug(featureSlug);
  const found = items.findIndex((_, i) => getFeatureSlug(locale, serviceId, i) === canonical);
  return found >= 0 ? found : null;
}

export function servicePath(locale: Locale, serviceId: ServiceId): string {
  return `/${locale}/${STATIC_SEGMENTS[locale].services}/${SERVICE_SLUGS[locale][serviceId]}`;
}

export function featurePath(locale: Locale, serviceId: ServiceId, featureIndex: number): string {
  return `${servicePath(locale, serviceId)}/${getFeatureSlug(locale, serviceId, featureIndex)}`;
}

export function pricingPath(locale: Locale): string {
  return `/${locale}/${STATIC_SEGMENTS[locale].pricing}`;
}

export function modulesPath(locale: Locale): string {
  return `${homePath(locale)}#modules`;
}

export function integrationsPath(locale: Locale): string {
  return `${homePath(locale)}#integrations`;
}

export function blogPath(locale: Locale): string {
  return `/${locale}/${STATIC_SEGMENTS[locale].blog}`;
}

export function blogPostPath(locale: Locale, slug: string): string {
  return `${blogPath(locale)}/${encodeURIComponent(slug)}`;
}

export function blogCategoryPath(locale: Locale, categorySlug: string): string {
  return `${blogPath(locale)}/kategori/${encodeURIComponent(categorySlug)}`;
}

export function blogListPath(locale: Locale, options?: { categorySlug?: string; page?: number }): string {
  const base = options?.categorySlug
    ? blogCategoryPath(locale, options.categorySlug)
    : blogPath(locale);
  const page = options?.page ?? 1;
  return page > 1 ? `${base}?page=${page}` : base;
}

export function tagPagePath(locale: Locale, tagSlug: string, page = 1): string {
  const base = tagPath(locale, tagSlug);
  return page > 1 ? `${base}?page=${page}` : base;
}

export function tagPath(locale: Locale, tagSlug: string): string {
  return `/${locale}/tag/${encodeURIComponent(normalizeTagSlug(tagSlug))}`;
}

export function contactPath(locale: Locale): string {
  return `/${locale}/${STATIC_SEGMENTS[locale].contact}`;
}

export function featuresPath(locale: Locale): string {
  return modulesPath(locale);
}

export function aboutPath(locale: Locale): string {
  return `/${locale}/${STATIC_SEGMENTS[locale].about}`;
}

export function privacyPath(locale: Locale): string {
  return `/${locale}/${STATIC_SEGMENTS[locale].privacy}`;
}

export function termsPath(locale: Locale): string {
  return `/${locale}/${STATIC_SEGMENTS[locale].terms}`;
}

export function faqPath(locale: Locale): string {
  return `${homePath(locale)}#faq`;
}

type ParsedPath =
  | { type: "home" }
  | { type: "service"; serviceId: ServiceId }
  | { type: "feature"; serviceId: ServiceId; featureIndex: number }
  | { type: "static"; key: keyof (typeof STATIC_SEGMENTS)["tr"] }
  | { type: "unknown" };

function parseLocalizedPath(pathname: string): { locale: Locale | null; parsed: ParsedPath } {
  const locale = parseLocale(pathname);
  if (!locale) return { locale: null, parsed: { type: "unknown" } };

  const rest = pathname.replace(/^\/(tr|en)/, "") || "/";
  if (rest === "/" || rest === "") return { locale, parsed: { type: "home" } };

  const segments = STATIC_SEGMENTS[locale];
  const serviceSegment = segments.services;
  const serviceMatch = rest.match(new RegExp(`^/${serviceSegment}/([^/]+)(?:/([^/]+))?$`));

  if (serviceMatch) {
    const serviceId = getServiceIdFromSlug(locale, serviceMatch[1]);
    if (!serviceId) return { locale, parsed: { type: "unknown" } };

    if (serviceMatch[2]) {
      const featureIndex = getFeatureIndexFromSlug(locale, serviceId, serviceMatch[2]);
      if (featureIndex === null) return { locale, parsed: { type: "service", serviceId } };
      return { locale, parsed: { type: "feature", serviceId, featureIndex } };
    }

    return { locale, parsed: { type: "service", serviceId } };
  }

  const staticKey = (Object.keys(segments) as Array<keyof typeof segments>).find(
    (key) => rest === `/${segments[key]}`
  );

  if (staticKey) return { locale, parsed: { type: "static", key: staticKey } };

  return { locale, parsed: { type: "unknown" } };
}

export function switchLocalePath(pathname: string, targetLocale: Locale): string {
  const hash = pathname.includes("#") ? pathname.slice(pathname.indexOf("#")) : "";
  const pathOnly = hash ? pathname.slice(0, pathname.indexOf("#")) : pathname;
  const { parsed } = parseLocalizedPath(pathOnly);

  switch (parsed.type) {
    case "home":
      return `${homePath(targetLocale)}${hash}`;
    case "service":
      return `${servicePath(targetLocale, parsed.serviceId)}${hash}`;
    case "feature":
      return `${featurePath(targetLocale, parsed.serviceId, parsed.featureIndex)}${hash}`;
    case "static":
      if (parsed.key === "blog" && !hasBlog(targetLocale)) {
        return `${homePath(targetLocale)}${hash}`;
      }
      return `/${targetLocale}/${STATIC_SEGMENTS[targetLocale][parsed.key]}${hash}`;
    default:
      return `${homePath(targetLocale)}${hash}`;
  }
}

export function getFooterLinks(locale: Locale) {
  const product = [
    { tr: "Özellikler", en: "Features", href: modulesPath(locale) },
    { tr: "Fiyatlandırma", en: "Pricing", href: pricingPath(locale) },
    { tr: "Entegrasyonlar", en: "Integrations", href: integrationsPath(locale) },
  ] as const;

  const services = SERVICE_IDS.map((id, index) => ({
    tr: SITE_CONTENT.tr.modTabs[index],
    en: SITE_CONTENT.en.modTabs[index],
    href: servicePath(locale, id),
  }));

  const resources = [
    ...(hasBlog(locale) ? [{ tr: "Blog", en: "Blog", href: blogPath(locale) }] : []),
    {
      tr: "Site haritası",
      en: "Site map",
      href: locale === "tr" ? "/tr/site-haritasi" : "/en/site-map",
    },
    { tr: "İletişim", en: "Contact", href: contactPath(locale) },
    { tr: "SSS", en: "FAQ", href: faqPath(locale) },
    { tr: "Hakkımızda", en: "About", href: aboutPath(locale) },
  ] as const;

  const bottom = [
    { tr: "Hakkımızda", en: "About", href: aboutPath(locale) },
    { tr: "Gizlilik Politikası", en: "Privacy Policy", href: privacyPath(locale) },
    { tr: "Kullanım Sözleşmesi", en: "Terms of Use", href: termsPath(locale) },
  ] as const;

  const company = [
    { tr: "Hakkımızda", en: "About", href: aboutPath(locale) },
    ...(hasBlog(locale) ? [{ tr: "Blog", en: "Blog", href: blogPath(locale) }] : []),
    { tr: "İletişim", en: "Contact", href: contactPath(locale) },
    { tr: "Gizlilik Politikası", en: "Privacy Policy", href: privacyPath(locale) },
    { tr: "Kullanım Sözleşmesi", en: "Terms", href: termsPath(locale) },
  ] as const;

  return { product, services, resources, bottom, company };
}
